<script setup lang="ts">
import { computed } from 'vue'
import { CMD_LANGUAGES } from '@/utils/ide'
import blocklyService from '@/services/ide/languages/blockly/blockly.service'
import { useIdeStore } from '@/stores/ide.store'
import { useTabStore } from '@/stores/tabs.store'
import { IDEVIEWTABS_MOBILE, TAB_ICONS_NAMES } from '@/utils/tabs'
import CodeSearchBar from '@/components/code/CodeSearchBar.vue'
import { TAB_BUTTON_POSITION } from '@/utils/tabs'

const props = defineProps({
  currentTabPosition: {
    type: String,
    required: false
  }
})

const ideStore = useIdeStore()
const tabStore = useTabStore()

const shareNotFound = computed(() => {
  return ideStore.shareNotFound
})
const interactiveMode = computed(() => {
  return ideStore.interactiveMode
})
const versions = computed(() => {
  return ideStore.isVersions
})
const showInputArg = computed(() => {
  if (!ideStore.isLanguage) {
    return false
  }
  return !CMD_LANGUAGES.includes(ideStore.isLanguage)
})

const isCurrentMobileTab = computed(() => {
  return ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.INPUT
})

const largeScreen = computed(() => {
  return (
    (tabStore.tabWidthCurrentSize.large &&
      tabStore.tabWidthCurrentSize.tabName == TAB_ICONS_NAMES.IO) ||
    (tabStore.tabWidthCurrentSizeBottom.large &&
      tabStore.tabWidthCurrentSizeBottom.tabName == TAB_ICONS_NAMES.IO)
  )
})

/**
 * Checks if the version is selected
 * @param index The version index to check
 * @returns True if the version is selected
 */
const isVersionSelected = (index: number) => {
  return ideStore.versionIndex === index
}
/**
 * Sets the version
 * @param e The event
 */
const setVersion = (e: Event) => {
  ideStore.setVersionIndex(parseInt((e.target as HTMLSelectElement).value))
  ideStore.setCodeUpdated(true)
  blocklyService.changeBlocklyLanguage()
}
</script>

<template>
  <div
    class="overflow-auto sm:overflow-hidden"
    :class="[
      'print:break-inside-avoid print:border print:p-1',
      { 'surface-secondary-base flex w-full flex-col px-4 sm:px-2': isCurrentMobileTab }, //If mobile tab,
      { '': !isCurrentMobileTab } //If NOT mobile tab
    ]"
    :id="`${IDEVIEWTABS_MOBILE.INPUT}-main`"
  >
    <!-- Toggles -->
    <div class="flex w-full" :class="largeScreen ? 'justify-start' : 'justify-center'">
      <div
        v-if="!shareNotFound"
        class="flex py-3 sm:pr-3 lg:pl-0 lg:pr-5"
        :class="[
          'h-auto print:break-inside-avoid print:border print:p-1',
          { 'w-full': isCurrentMobileTab }, //If mobile tab,
          { 'w-fit ': !isCurrentMobileTab }, //If NOT mobile tab
          largeScreen ? 'flex-row gap-7' : 'flex-col gap-0',
          isCurrentMobileTab ? 'gap-5' : ''
        ]"
      >
        <!-- Language Search/Select-->
        <div v-if="isCurrentMobileTab" class="flex w-full flex-col gap-2 print:hidden">
          <p for="languageSelect" class="w- text-xs">Language:</p>
          <CodeSearchBar :is-mini="true" :is-full-size="false" />
        </div>

        <!-- Language Version Dropdown -->
        <div
          class="flex w-full gap-2 print:hidden"
          :class="[
            {
              'flex-col': isCurrentMobileTab
            },
            {
              'flex-row items-center justify-between ': !isCurrentMobileTab
            }
          ]"
        >
          <label for="versionSelect" class="text-high w-max pr-2 text-sm font-normal leading-snug"
            >Language version:</label
          >
          <select
            id="versionSelect"
            class="background-primary block h-full min-w-20 rounded-md py-2 text-[10px] sm:h-fit print:border"
            :class="[
              {
                'w-full p-2': isCurrentMobileTab
              },
              {
                'w-auto pl-2': !isCurrentMobileTab
              }
            ]"
            @change="setVersion($event)"
          >
            <option
              v-for="(version, index) in versions"
              :key="index"
              :selected="isVersionSelected(index)"
              :value="index"
              class="w-full"
            >
              {{ version }}
            </option>
          </select>
        </div>
        <!-- Interactive Toggle -->
        <div class="flex w-full items-center">
          <input
            type="checkbox"
            class="h-4 w-4 accent-black"
            id="interactiveToggle"
            v-model="ideStore.interactiveMode"
          />
          <label
            for="input-check"
            class="text-high min-w-0 pl-4 text-sm font-normal"
            :class="largeScreen || ideStore.currentMobileTab ? 'pl-4' : ''"
            >Interactive Mode</label
          >
          <div class="hs-tooltip flex py-2 [--placement:top]">
            <a
              id="new-project-ide"
              rel="nofollow"
              href="https://www.jdoodle.com/docs/ide/what-is-interactive-mode-in-jdoodle"
              target="_blank"
              class="hs-tooltip-toggle ms-2 pb-1"
            >
              <FontAwesomeIcon
                icon="fa-circle-info"
                class="color-secondary-700 h-2 w-2 sm:h-2.5 sm:w-2.5"
              ></FontAwesomeIcon>
            </a>

            <span
              class="hs-tooltip-content invisible absolute z-10 inline-block max-w-64 rounded bg-gray-900 px-2 py-1 text-center text-xs font-medium text-white opacity-0 shadow-sm transition-opacity hs-tooltip-shown:visible hs-tooltip-shown:opacity-100 dark:bg-slate-700"
              role="tooltip"
            >
              Add inputs directly in the output box
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- START body container -->
    <div class="flex w-full sm:h-[70%] sm:overflow-hidden">
      <div
        :class="[
          'flex h-full w-full flex-col gap-1 lg:h-auto ',
          {
            'justify-between lg:flex-col': interactiveMode
          }
        ]"
      >
        <!-- INPUT TEXTBOX -->
        <div
          v-show="showInputArg"
          :class="[
            'h-fit w-full gap-2',
            {
              'sm:h-full ': interactiveMode
            }
          ]"
        >
          <textarea
            placeholder="Input arguments"
            :class="[
              'p-small bloc background-primary stroke-secondary-default h-10 w-full resize-none rounded border p-2 outline-none print:border',
              {
                'h-full': interactiveMode
              },

              {
                'min-h-auto': !isCurrentMobileTab
              },
              {
                'max-h-full':
                  !isCurrentMobileTab &&
                  interactiveMode &&
                  props.currentTabPosition == TAB_BUTTON_POSITION.BOTTOM
              }
            ]"
            :value="ideStore.args || ''"
            @input="(e: Event) => { ideStore.args = (e.target as HTMLInputElement).value }"
          />
        </div>

        <!-- STDIN INPUT TEXTBOX -->
        <div
          v-if="!interactiveMode"
          :class="[
            'h-full w-full print:block print:border ',
            { 'pb-4': isCurrentMobileTab } //If mobile tab
          ]"
        >
          <textarea
            placeholder="Stdin Inputs"
            :class="[
              'p-small background-primary stroke-secondary-default block h-full w-full resize-none rounded border p-2 outline-none print:border'
            ]"
            :value="ideStore.stdin || ''"
            rows="5"
            @input="(e: Event) => { ideStore.stdin = (e.target as HTMLInputElement).value }"
          />
        </div>

        <!-- Hidden - Print only -->
        <div
          v-if="interactiveMode"
          class="hidden min-h-fit rounded-lg px-1 print:block print:border"
        >
          <label class="p-normal my-2 block px-3"
            >Interactive Mode: {{ interactiveMode ? 'On' : 'Off' }}</label
          >
        </div>
      </div>
    </div>
  </div>
</template>
